import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import {
  type Exception,
  type ExceptionTableFilters,
  defaultFilterObject,
  useGetOfficeExceptionsQuery,
} from '@frontend/api-schedule-exceptions';
import { ScopedSingleLocationFilterMenu } from '@frontend/location-filter-menu';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useFormField } from '@frontend/design-system';
import { DirectAvailabilityCheck } from '../../components/DirectAvailabilityCheck';
import { ScheduleBanner } from '../../components/ScheduleBanner';
import { ScheduleExceptions } from '../../components/ScheduleExceptions';
import { scheduleDirectAvailabilityBannerText } from '../../constants';

const { officeHoursDirectAvailabilityTitle, officeHoursDirectAvailabilityMessage } =
  scheduleDirectAvailabilityBannerText;

export const OfficeHoursExceptionsV2 = () => {
  const { selectedLocationIds, isSingleTypeScope } = useAppScopeStore();

  const selectedLocationField = useFormField({ type: 'checklist', value: [selectedLocationIds[0]] }, [
    selectedLocationIds,
  ]);

  const hasMultipleLocations = !isSingleTypeScope;

  const [filters, setFilters] = useState(defaultFilterObject);

  const locationId = hasMultipleLocations ? selectedLocationField.value[0] : selectedLocationIds[0];

  const {
    data: exceptionData,
    refetch: refetchExceptions,
    isLoading: isLoadingExceptions,
  } = useGetOfficeExceptionsQuery({
    locationIds: [locationId],
    ...(filters.dateRange[0] && { startDateTime: dayjs(`${filters.dateRange[0]} ${filters.timeRange[0]}`).format() }),
    ...(filters.dateRange[1] && { endDateTime: dayjs(`${filters.dateRange[1]} ${filters.timeRange[1]}`).format() }),
  });

  const handleFiltersUpdate = (newFilters: ExceptionTableFilters) => {
    setFilters(newFilters);
  };

  const filterByDateTime = () => {
    if (!filters.timeRange[0].length || filters.isAllDay) {
      setFilters({ ...filters, timeRange: ['00:00:00', '11:59:00'] });
    }
  };

  const handleFilterDateRangeChange = async () => {
    await filterByDateTime();
    refetchExceptions();
  };

  useEffect(() => {
    if (filters.dateRange[0]) {
      handleFilterDateRangeChange();
    }
  }, [filters.dateRange[0]]);

  return (
    <>
      {hasMultipleLocations && (
        <>
          <ScopedSingleLocationFilterMenu field={selectedLocationField} css={locationDropdownStyle} />
          <DirectAvailabilityCheck locationId={selectedLocationField.value[0]} css={{ marginTop: theme.spacing(2) }}>
            <ScheduleBanner
              fullWidth
              title={officeHoursDirectAvailabilityTitle}
              message={officeHoursDirectAvailabilityMessage}
            />
          </DirectAvailabilityCheck>
        </>
      )}

      <ScheduleExceptions
        locationId={selectedLocationField.value[0]}
        data={exceptionData?.data as Exception[]}
        filters={filters}
        handleFiltersUpdate={handleFiltersUpdate}
        isLoading={isLoadingExceptions}
      />
    </>
  );
};

const locationDropdownStyle = css({
  marginTop: theme.spacing(4),
  background: theme.colors.primary50,
  color: theme.colors.white,
  p: { color: theme.colors.white },
});
